import { useEffect, useMemo, useState } from "react";

const API = "/api";

const emptyProduct = {
  id: "",
  slug: "",
  name: "",
  strain: "green",
  color: "Green",
  short_desc: "",
  description: "",
  effects: "",
  image: "",
  gallery: "",
  variants: "50g|19.99|KV-50G\n100g|34.99|KV-100G",
  badges: "",
  featured: false,
  rating: 4.8,
  reviews_count: 0,
};

export default function Admin() {
  const token = localStorage.getItem("kv_token") || "";
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState("dashboard");

  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [settings, setSettings] = useState({});
  const [products, setProducts] = useState([]);
  const [media, setMedia] = useState([]);
  const [mediaUploads, setMediaUploads] = useState([]);
  const [mediaUploadStatus, setMediaUploadStatus] = useState("");
  const [mediaStatusFading, setMediaStatusFading] = useState(false);
  const [mediaViewer, setMediaViewer] = useState(null);
  const [productMediaPicker, setProductMediaPicker] = useState(null);
  const [imageSourcePicker, setImageSourcePicker] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [nowTick, setNowTick] = useState(Date.now());
  const [statusFilter, setStatusFilter] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadStatusFading, setUploadStatusFading] = useState(false);
  const [settingUploadStatus, setSettingUploadStatus] = useState("");

  const emptyPromo = {
    title: "",
    code: "",
    type: "percent",
    value: 10,
    active: true,
    applies_to: "all",
    product_ids: [],
    start_at: "",
    end_at: "",
    show_banner: false,
    banner_text: "",
    minimum_order: 0,
  };

  const [promoForm, setPromoForm] = useState(emptyPromo);
  const [editingPromo, setEditingPromo] = useState(null);

  const authHeaders = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  async function apiGet(url) {
    const r = await fetch(url, { headers: authHeaders });
    return await r.json();
  }

  async function loadDashboard() {
    setData(await apiGet(`${API}/admin/dashboard`));
  }

  async function loadOrders() {
    const qs = new URLSearchParams();
    if (paymentFilter !== "all") qs.set("payment_status", paymentFilter);
    if (statusFilter !== "all") qs.set("status", statusFilter);
    if (orderSearch.trim()) qs.set("q", orderSearch.trim());

    const url = `${API}/admin/orders${qs.toString() ? `?${qs.toString()}` : ""}`;
    setOrders(await apiGet(url));
  }

  async function loadPromos() {
    setPromos(await apiGet(`${API}/admin/promotions`));
  }

  async function loadSettings() {
    setSettings(await apiGet(`${API}/admin/settings`));
  }

  async function loadProducts() {
    setProducts(await apiGet(`${API}/products`));
  }

  async function loadMedia() {
    setMedia(await apiGet(`${API}/admin/uploads`));
  }

  useEffect(() => {
    async function checkAdmin() {
      if (!token) {
        setAuthChecked(true);
        return;
      }
      try {
        const r = await fetch(`${API}/auth/me`, { headers: authHeaders });
        setMe(await r.json());
      } catch {
        setMe(null);
      } finally {
        setAuthChecked(true);
      }
    }
    checkAdmin();
  }, [token, authHeaders]);

  useEffect(() => {
    if (!authChecked || me?.role !== "admin") return;
    if (tab === "dashboard") loadDashboard();
    if (tab === "orders") loadOrders();
    if (tab === "promotions") loadPromos();
    if (tab === "settings") loadSettings();
    if (tab === "products") loadProducts();
    if (tab === "media") loadMedia();
  }, [tab, authChecked, me, paymentFilter, statusFilter, orderSearch]);


  useEffect(() => {
    if (!uploadStatus) return;
    if (!uploadStatus.toLowerCase().includes("success")) return;

    setUploadStatusFading(false);

    const fadeTimer = setTimeout(() => {
      setUploadStatusFading(true);
    }, 2200);

    const clearTimer = setTimeout(() => {
      setUploadStatus("");
      setUploadStatusFading(false);
    }, 3200);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(clearTimer);
    };
  }, [uploadStatus]);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!authChecked || me?.role !== "admin" || tab !== "orders") return;

    const t = setInterval(() => {
      loadOrders();
    }, 20000);

    return () => clearInterval(t);
  }, [tab, authChecked, me, paymentFilter, statusFilter]);

  function slugify(v) {
    return v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  async function deleteUploadIfLocal(pathValue) {
    if (!pathValue || !pathValue.startsWith("/uploads/")) return;

    try {
      await fetch(`${API}/admin/upload?path=${encodeURIComponent(pathValue)}`, {
        method: "DELETE",
        headers: authHeaders,
      });
    } catch {}
  }



  function openImageSourcePicker(target) {
    setImageSourcePicker(target);
  }

  function chooseLocalUploadSource() {
    const id = imageSourcePicker === "main"
      ? "product-main-local-file"
      : "product-gallery-local-file";

    setImageSourcePicker(null);

    setTimeout(() => {
      document.getElementById(id)?.click();
    }, 50);
  }

  function chooseMediaGallerySource() {
    const target = imageSourcePicker;
    setImageSourcePicker(null);

    if (target) {
      openProductMediaPicker(target);
    }
  }

  function openProductMediaPicker(target) {
    setProductMediaPicker(target);
    loadMedia();
  }

  function chooseProductMedia(file) {
    if (!file?.url || !productMediaPicker) return;

    if (productMediaPicker === "main") {
      setProductForm((x) => ({ ...x, image: file.url }));
      showProductUploadStatus("Main image selected from Media Gallery", true);
    }

    if (productMediaPicker === "gallery") {
      setProductForm((x) => {
        const current = x.gallery
          .split("\n")
          .map(v => v.trim())
          .filter(Boolean);

        if (!current.includes(file.url)) current.push(file.url);

        return { ...x, gallery: current.join("\n") };
      });

      showProductUploadStatus("Gallery image selected from Media Gallery", true);
    }

    setProductMediaPicker(null);
  }

  function galleryItems() {
    return productForm.gallery
      .split("\n")
      .map(x => x.trim())
      .filter(Boolean);
  }

  async function removeGalleryImage(index) {
    const items = galleryItems();
    const removed = items[index];
    const nextItems = items.filter((_, i) => i !== index);

    await deleteUploadIfLocal(removed);

    setProductForm(x => {
      let nextImage = x.image;

      if (x.image === removed) {
        nextImage = nextItems[0] || "";
      }

      return {
        ...x,
        image: nextImage,
        gallery: nextItems.join("\n"),
      };
    });
  }

  async function removeMainImage() {
    const oldImage = productForm.image;
    await deleteUploadIfLocal(oldImage);

    const firstGallery = galleryItems().find(x => x !== oldImage) || "";

    setProductForm(x => ({
      ...x,
      image: firstGallery,
    }));
  }

  function productPayload() {
    return {
      id: productForm.id || crypto.randomUUID(),
      slug: productForm.slug || slugify(productForm.name),
      name: productForm.name,
      strain: productForm.strain,
      color: productForm.color,
      short_desc: productForm.short_desc,
      description: productForm.description,
      effects: productForm.effects.split(",").map(x => x.trim()).filter(Boolean),
      image: productForm.image || productForm.gallery.split("\n").map(x => x.trim()).filter(Boolean)[0] || "",
      gallery: productForm.gallery.split("\n").map(x => x.trim()).filter(Boolean),
      variants: productForm.variants.split("\n").map(line => {
        const [weight, price, sku] = line.split("|").map(x => x.trim());
        return { weight, price: Number(price || 0), sku };
      }).filter(v => v.weight && v.sku),
      badges: productForm.badges.split(",").map(x => x.trim()).filter(Boolean),
      featured: !!productForm.featured,
      rating: Number(productForm.rating || 4.8),
      reviews_count: Number(productForm.reviews_count || 0),
    };
  }

  function editProduct(p) {
    setEditingProduct(p.id);
    setProductForm({
      id: p.id || "",
      slug: p.slug || "",
      name: p.name || "",
      strain: p.strain || "green",
      color: p.color || "",
      short_desc: p.short_desc || "",
      description: p.description || "",
      effects: (p.effects || []).join(", "),
      image: p.image || "",
      gallery: (p.gallery || []).join("\n"),
      variants: (p.variants || []).map(v => `${v.weight}|${v.price}|${v.sku}`).join("\n"),
      badges: (p.badges || []).join(", "),
      featured: !!p.featured,
      rating: p.rating || 4.8,
      reviews_count: p.reviews_count || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function saveProduct() {
    const payload = productPayload();
    const isEdit = !!editingProduct;

    await fetch(`${API}/admin/products${isEdit ? `/${editingProduct}` : ""}`, {
      method: isEdit ? "PUT" : "POST",
      headers: authHeaders,
      body: JSON.stringify(payload),
    });

    setEditingProduct(null);
    setProductForm(emptyProduct);
    loadProducts();
    alert(isEdit ? "Product updated" : "Product created");
  }

  async function deleteProduct(id) {
    if (!confirm("Delete this product?")) return;
    await fetch(`${API}/admin/products/${id}`, {
      method: "DELETE",
      headers: authHeaders,
    });
    loadProducts();
  }

  function uploadFileWithProgress(file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fd = new FormData();
      fd.append("file", file);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        try {
          const json = JSON.parse(xhr.responseText || "{}");
          if (xhr.status >= 200 && xhr.status < 300 && json.url) {
            resolve(json);
          } else {
            reject(new Error(json.detail || "Upload failed"));
          }
        } catch {
          reject(new Error("Upload failed"));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));

      xhr.open("POST", `${API}/admin/upload`);

      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      xhr.send(fd);
    });
  }


  function showProductUploadStatus(message, autoHide = false) {
    setUploadStatusFading(false);
    setUploadStatus(message);

    if (autoHide) {
      setTimeout(() => setUploadStatusFading(true), 2200);
      setTimeout(() => {
        setUploadStatus("");
        setUploadStatusFading(false);
      }, 3200);
    }
  }

  async function uploadImage(e, field) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    try {
      if (field === "image") {
        const file = files[0];
        showProductUploadStatus(`Uploading main image 0%`);

        const j = await uploadFileWithProgress(file, (p) => {
          showProductUploadStatus(`Uploading main image ${p}%`);
        });

        setProductForm(x => ({ ...x, image: j.url }));
        showProductUploadStatus("Main image uploaded successfully", true);
      }

      if (field === "gallery") {
        const uploaded = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          showProductUploadStatus(`Uploading gallery ${i + 1}/${files.length} - 0%`);

          const j = await uploadFileWithProgress(file, (p) => {
            showProductUploadStatus(`Uploading gallery ${i + 1}/${files.length} - ${p}%`);
          });

          uploaded.push(j.url);
        }

        setProductForm(x => ({
          ...x,
          gallery: `${x.gallery ? x.gallery + "\n" : ""}${uploaded.join("\n")}`
        }));

        showProductUploadStatus(`${uploaded.length} gallery image(s) uploaded successfully`, true);
      }
    } catch (err) {
      showProductUploadStatus(err.message || "Upload failed");
      alert(err.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  }

  async function uploadSettingImage(e, field) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSettingUploadStatus("Uploading banner 0%");

      const j = await uploadFileWithProgress(file, (p) => {
        setSettingUploadStatus(`Uploading banner ${p}%`);
      });

      setSettings(x => ({ ...x, [field]: j.url }));
      setSettingUploadStatus("Banner uploaded successfully");
    } catch (err) {
      setSettingUploadStatus(err.message || "Upload failed");
      alert(err.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  }

  async function uploadSettingImage(e, field) {
    const file = e.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("file", file);

    const r = await fetch(`${API}/admin/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });

    const j = await r.json();

    if (!j.url) {
      alert(j.detail || "Upload failed");
      return;
    }

    setSettings(x => ({ ...x, [field]: j.url }));
  }


  function formatWIB(ts) {
    if (!ts) return "-";
    const d = new Date(ts * 1000);
    return d.toLocaleString("id-ID", {
      timeZone: "Asia/Jakarta",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }) + " WIB";
  }

  function countdown(ts) {
    if (!ts) return "-";
    const diff = ts * 1000 - Date.now();
    if (diff <= 0) return "Expired";

    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const sec = Math.floor((diff % 60000) / 1000);
    return `${h}h ${m}m ${sec}s`;
  }



  function formatWIB(ts) {
    if (!ts) return "-";
    const d = new Date(Number(ts) * 1000);
    return d.toLocaleString("id-ID", {
      timeZone: "Asia/Jakarta",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }) + " WIB";
  }

  function stripeCountdown(ts) {
    if (!ts) return "-";
    const diff = Number(ts) * 1000 - nowTick;
    if (diff <= 0) return "Expired";

    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const sec = Math.floor((diff % 60000) / 1000);
    return `${h}h ${m}m ${sec}s`;
  }

  function stripeCountdownColor(ts) {
    if (!ts) return "#aaa";
    const diff = Number(ts) * 1000 - nowTick;
    if (diff <= 0) return "#ff7777";
    if (diff < 3600000) return "#ffcc66";
    return "#6ee784";
  }

  async function syncOrderPayments() {
    await fetch(`${API}/admin/orders/sync-payments`, {
      method: "POST",
      headers: authHeaders,
    });
    loadOrders();
  }


  async function deleteOrder(id) {
    if (!id) return;
    if (!confirm("Delete this order permanently? This removes it from admin and user order history.")) return;

    const r = await fetch(`${API}/admin/orders/${id}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    if (!r.ok) {
      alert(await r.text());
      return;
    }

    setSelectedOrder(null);
    loadOrders();
  }

  async function verifyBankOrder(id) {
    if (!id) return;
    if (!confirm("Mark this bank transfer as PAID and move order to processing?")) return;

    const r = await fetch(`${API}/admin/orders/${id}/verify-bank`, {
      method: "POST",
      headers: authHeaders,
    });

    if (!r.ok) {
      alert(await r.text());
      return;
    }

    setSelectedOrder(null);
    loadOrders();
  }

  async function rejectBankOrder(id) {
    if (!id) return;
    if (!confirm("Reject this bank payment proof?")) return;

    const r = await fetch(`${API}/admin/orders/${id}/reject-bank`, {
      method: "POST",
      headers: authHeaders,
    });

    if (!r.ok) {
      alert(await r.text());
      return;
    }

    setSelectedOrder(null);
    loadOrders();
  }

  function openOrder(o) {
    setSelectedOrder({
      ...o,
      status: o.status || "pending",
      payment_status: o.payment_status || "initiated",
      courier: o.courier || "DHL Express",
      tracking_number: o.tracking_number || "",
      admin_note: o.admin_note || "",
    });
  }

  async function saveSelectedOrder() {
    if (!selectedOrder?.id) return;

    await fetch(`${API}/admin/orders/${selectedOrder.id}`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({
        status: selectedOrder.status,
        payment_status: selectedOrder.payment_status,
        courier: selectedOrder.courier,
        tracking_number: selectedOrder.tracking_number,
        admin_note: selectedOrder.admin_note,
      }),
    });

    alert("Order updated");
    setSelectedOrder(null);
    loadOrders();
    loadDashboard();
  }

  function editPromo(promo) {
    setEditingPromo(promo.id);
    setPromoForm({
      title: promo.title || "",
      code: promo.code || "",
      type: promo.type || "percent",
      value: promo.value || 0,
      active: promo.active !== false,
      applies_to: promo.applies_to || "all",
      product_ids: promo.product_ids || [],
      start_at: promo.start_at || "",
      end_at: promo.end_at || "",
      show_banner: !!promo.show_banner,
      banner_text: promo.banner_text || "",
      minimum_order: promo.minimum_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function savePromo() {
    if (!promoForm.title) {
      alert("Promo title is required");
      return;
    }

    const payload = {
      ...promoForm,
      code: promoForm.code ? promoForm.code.trim().toUpperCase() : null,
      value: Number(promoForm.value || 0),
      minimum_order: Number(promoForm.minimum_order || 0),
      start_at: promoForm.start_at || null,
      end_at: promoForm.end_at || null,
    };

    await fetch(`${API}/admin/promotions${editingPromo ? `/${editingPromo}` : ""}`, {
      method: editingPromo ? "PUT" : "POST",
      headers: authHeaders,
      body: JSON.stringify(payload),
    });

    setEditingPromo(null);
    setPromoForm(emptyPromo);
    loadPromos();
    alert(editingPromo ? "Promo updated" : "Promo created");
  }

  async function togglePromo(promo) {
    await fetch(`${API}/admin/promotions/${promo.id}`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({
        ...promo,
        active: !promo.active,
      }),
    });

    loadPromos();
  }

  async function togglePromoBanner(promo) {
    await fetch(`${API}/admin/promotions/${promo.id}`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({
        ...promo,
        show_banner: !promo.show_banner,
      }),
    });

    loadPromos();
  }

  async function deletePromo(id) {
    if (!confirm("Delete this promo permanently?")) return;

    await fetch(`${API}/admin/promotions/${id}`, {
      method: "DELETE",
      headers: authHeaders,
    });

    loadPromos();
  }



  function showMediaStatus(message, autoHide = false) {
    setMediaStatusFading(false);
    setMediaUploadStatus(message);

    if (autoHide) {
      setTimeout(() => setMediaStatusFading(true), 2200);
      setTimeout(() => {
        setMediaUploadStatus("");
        setMediaStatusFading(false);
      }, 3200);
    }
  }

  function uploadMediaFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    files.forEach((file) => {
      const id = `${Date.now()}-${file.name}-${Math.random()}`;

      showMediaStatus(`Uploading ${file.name} 0%`);

      setMediaUploads((prev) => [
        ...prev,
        { id, name: file.name, progress: 0, status: "uploading", message: "Uploading..." }
      ]);

      const formData = new FormData();
      formData.append("file", file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/admin/upload`);

      Object.entries(authHeaders || {}).forEach(([key, value]) => {
        if (key.toLowerCase() !== "content-type") {
          xhr.setRequestHeader(key, value);
        }
      });

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const progress = Math.round((event.loaded / event.total) * 100);
        showMediaStatus(`Uploading ${file.name} ${progress}%`);
        setMediaUploads((prev) =>
          prev.map((x) => x.id === id ? { ...x, progress } : x)
        );
      };

      xhr.onload = () => {
        const ok = xhr.status >= 200 && xhr.status < 300;

        setMediaUploads((prev) =>
          prev.map((x) =>
            x.id === id
              ? {
                  ...x,
                  progress: ok ? 100 : x.progress,
                  status: ok ? "success" : "error",
                  message: ok ? "Upload success" : `Upload failed (${xhr.status})`
                }
              : x
          )
        );

        if (ok) {
          loadMedia();
          showMediaStatus(`${file.name} uploaded successfully`, true);
        }
      };

      xhr.onerror = () => {
        showMediaStatus(`Upload failed: ${file.name}`);
        setMediaUploads((prev) =>
          prev.map((x) =>
            x.id === id
              ? { ...x, status: "error", message: "Upload failed" }
              : x
          )
        );
      };

      xhr.send(formData);
    });
  }


  async function deleteMediaFile(url) {
    if (!url) return;

    try {
      const r = await fetch(`${API}/admin/upload?path=${encodeURIComponent(url)}`, {
        method: "DELETE",
        headers: authHeaders,
      });

      if (!r.ok) {
        const text = await r.text();
        alert(`Delete failed (${r.status}): ${text}`);
        return;
      }

      setMedia((prev) => prev.filter((x) => x.url !== url));
      if (mediaViewer?.url === url) setMediaViewer(null);
      showMediaStatus("Image deleted successfully", true);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  }


  function confirmDeleteMediaFile(file) {
    if (!file?.url) return;

    const usageList = Array.isArray(file?.usage) ? file.usage : [];
    const usageText = usageList.length ? usageList.join(" + ") : "Unused";
    const warning = file?.is_used
      ? `This image is used by: ${usageText}.\n\nDelete it permanently anyway?`
      : "Delete this file permanently from VPS?";

    if (window.confirm(warning)) {
      deleteMediaFile(file.url);
    }
  }

  function formatBytes(bytes) {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
  }

  async function saveSettings() {
    await fetch(`${API}/admin/settings`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(settings),
    });
    alert("Saved");
  }

  if (!authChecked) return <AdminShell>Checking admin access...</AdminShell>;

  if (!token) {
    return (
      <AdminShell>
        <h1 style={h1}>Admin Login Required</h1>
        <p style={muted}>Please login with an admin account first.</p>
        <a href="/login" style={link}>Go to Login</a>
      </AdminShell>
    );
  }

  if (me?.role !== "admin") {
    return (
      <AdminShell>
        <h1 style={h1}>Access Denied</h1>
        <p style={muted}>Your account is not allowed to access the admin panel.</p>
        <a href="/" style={link}>Back to Store</a>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <div style={tabs}>
        {["dashboard", "products", "orders", "promotions", "settings", "media"].map(x => (
          <button key={x} onClick={() => setTab(x)} style={tabBtn(tab === x)}>
            {x}
          </button>
        ))}
      </div>

      {tab === "dashboard" && data && (
        <div style={grid}>
          <Card title="Revenue" value={`$${data.revenue || 0}`} />
          <Card title="Orders" value={data.total_orders || 0} />
          <Card title="Paid Orders" value={data.paid_orders || 0} />
          <Card title="Users" value={data.users || 0} />
          <Card title="Products" value={data.products || 0} />
        </div>
      )}

      {tab === "products" && (
        <div>
          <h2 style={h2}>{editingProduct ? "Edit Product" : "Add Product"}</h2>

          <div style={formGrid}>
            <Input label="Name" value={productForm.name} onChange={v => setProductForm({...productForm, name:v, slug: slugify(v)})} />
            <Input label="Slug" value={productForm.slug} onChange={v => setProductForm({...productForm, slug:v})} />
            <Input label="Strain" value={productForm.strain} onChange={v => setProductForm({...productForm, strain:v})} />
            <Input label="Color" value={productForm.color} onChange={v => setProductForm({...productForm, color:v})} />
            <Input label="Short Description" value={productForm.short_desc} onChange={v => setProductForm({...productForm, short_desc:v})} />
            <Input label="Badges comma separated" value={productForm.badges} onChange={v => setProductForm({...productForm, badges:v})} />
          </div>

          <TextArea label="Description" value={productForm.description} onChange={v => setProductForm({...productForm, description:v})} />
          <Input label="Effects comma separated" value={productForm.effects} onChange={v => setProductForm({...productForm, effects:v})} />

          <div style={formGrid}>
            <div>
              <label style={label}>Main Image</label>
              <input
                style={input}
                value={productForm.image}
                onChange={e => setProductForm({...productForm, image:e.target.value})}
                placeholder="Leave empty to use first gallery image / default"
              />

              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:10 }}>
                <input
                  id="product-main-local-file"
                  style={{ display:"none" }}
                  type="file"
                  accept="image/*"
                  onChange={e => uploadImage(e, "image")}
                />
                <div className="clean-file-picker">
                  <button
                    type="button"
                    className="clean-file-button"
                    onClick={() => openImageSourcePicker("main")}
                  >
                    Choose File
                  </button>
                  <span>No file chosen</span>
                </div>

                {productForm.image && (
                  <button type="button" style={dangerBtn} onClick={removeMainImage}>
                    Remove Main Image
                  </button>
                )}
              </div>

              {productForm.image ? (
                <img
                  alt=""
                  src={productForm.image}
                  style={preview}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div style={emptyImageBox}>No main image. First gallery image or default image will be used.</div>
              )}
            </div>

            <div>
              <label style={label}>Gallery Images</label>
              <textarea
                style={textarea}
                value={productForm.gallery}
                onChange={e => setProductForm({...productForm, gallery:e.target.value})}
                placeholder="One image URL per line"
              />

              <div style={{ marginTop:10 }}>
                <input
                  id="product-gallery-local-file"
                  style={{ display:"none" }}
                  multiple
                  type="file"
                  accept="image/*"
                  onChange={e => uploadImage(e, "gallery")}
                />
                <div className="clean-file-picker">
                  <button
                    type="button"
                    className="clean-file-button"
                    onClick={() => openImageSourcePicker("gallery")}
                  >
                    Choose File
                  </button>
                  <span>No file chosen</span>
                </div>
                <div style={{ color:"#aaa", fontSize:12, marginTop:6 }}>
                  You can select multiple gallery images at once.
                </div>
              </div>

              
            </div>
          </div>


          {imageSourcePicker && (
            <div className="image-choice-overlay" onClick={() => setImageSourcePicker(null)}>
              <div className="image-choice-modal" onClick={(e) => e.stopPropagation()}>
                <div className="image-choice-head">
                  <h3>{imageSourcePicker === "main" ? "Choose Main Image" : "Choose Gallery Image"}</h3>
                  <button type="button" onClick={() => setImageSourcePicker(null)}>×</button>
                </div>

                <p>
                  Select where you want to get the image from.
                </p>

                <div className="image-choice-actions">
                  <button type="button" onClick={chooseLocalUploadSource}>
                    Upload from Local PC
                  </button>

                  <button type="button" onClick={chooseMediaGallerySource}>
                    Choose from Media Gallery
                  </button>
                </div>
              </div>
            </div>
          )}

          {uploadStatus && (
            <div style={{
              margin:"12px 0",
              padding:12,
              background:"#0b0b0b",
              border:"1px solid #333",
              borderRadius:10,
              color: uploadStatus.toLowerCase().includes("failed") ? "#FCA5A5" : "#C8A97E",
              fontWeight:700,
              opacity: mediaStatusFading ? 0 : 1,
              transition:"opacity .9s ease"
            }}>
              {uploadStatus}
            </div>
          )}
          {galleryItems().length > 0 && (
            <div style={{ margin:"16px 0" }}>
              <div style={label}>Gallery Preview</div>

              <div className="product-gallery-grid">
                {galleryItems().map((url, i) => (
                  <div key={`${url}-${i}`} className="product-gallery-card">
                    <img
                      src={url}
                      alt=""
                      className="product-gallery-img"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const holder = e.currentTarget.nextSibling;
                        if (holder) holder.style.display = "flex";
                      }}
                    />

                    <div className="product-gallery-missing">
                      Image not found
                    </div>

                    <div className="product-gallery-actions">
                      <button
                        type="button"
                        onClick={() => setProductForm({...productForm, image:url})}
                      >
                        Set Main
                      </button>

                      <button
                        type="button"
                        className="danger"
                        onClick={() => removeGalleryImage(i)}
                      >
                        Delete
                      </button>
                    </div>

                    {productForm.image === url && (
                      <div className="main-badge">MAIN</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}



          {productMediaPicker && (
            <div className="media-viewer" onClick={() => setProductMediaPicker(null)}>
              <div className="media-viewer-inner product-media-picker" onClick={(e) => e.stopPropagation()}>
                <div className="admin-orders-toolbar">
                  <h3>
                    Choose image for {productMediaPicker === "main" ? "Main Image" : "Gallery"}
                  </h3>
                  <button style={smallBtn} onClick={() => setProductMediaPicker(null)}>Close</button>
                </div>

                {media.length === 0 && <p style={muted}>No media files found.</p>}

                <div className="media-grid">
                  {media.map((file) => (
                    <div key={file.url} className="media-card">
                      <div className="media-preview">
                        <img
                          src={`${file.url}?v=${file.modified_at || Date.now()}`}
                          alt={file.name}
                        />
                      </div>

                      <div className="media-info">
                        <b title={file.name}>{file.name}</b>
                        <span>{formatBytes(file.size)}</span>
                      </div>

                      <div className="media-actions">
                        <button type="button" style={primaryBtn} onClick={() => chooseProductMedia(file)}>
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <TextArea
            label="Variants: weight|price|sku, one per line"
            value={productForm.variants}
            onChange={v => setProductForm({...productForm, variants:v})}
          />

          <label className="product-feature-card">
            <input
              type="checkbox"
              checked={productForm.featured}
              onChange={e => setProductForm({...productForm, featured:e.target.checked})}
            />
            <span>
              <b>Featured Product</b>
              <small>Show this product on homepage featured section</small>
            </span>
          </label>

          <div style={{ display:"flex", gap:10, marginBottom:30 }}>
            <button style={primaryBtn} onClick={saveProduct}>{editingProduct ? "Update Product" : "Create Product"}</button>
            {editingProduct && <button style={dangerBtn} onClick={() => { setEditingProduct(null); setProductForm(emptyProduct); }}>Cancel Edit</button>}
          </div>

          <h2 style={h2}>Product List</h2>
          {products.map(p => (
            <div key={p.id} style={row}>
              <div style={{ display:"flex", gap:15, alignItems:"center" }}>
                {p.image && <img alt="" src={p.image} style={{ width:70, height:70, objectFit:"cover", borderRadius:10 }} />}
                <div>
                  <b>{p.name}</b><br />
                  <span style={muted}>/{p.slug}</span><br />
                  <span style={muted}>{p.variants?.length || 0} variants • {p.featured ? "Featured" : "Normal"}</span>
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button style={smallBtn} onClick={() => editProduct(p)}>Edit</button>
                <button style={dangerBtn} onClick={() => deleteProduct(p.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "orders" && (
        <div>
          <div className="admin-orders-toolbar">
            <h2 style={h2}>Orders</h2>

            <div className="admin-filter-group">
              <input
                style={input}
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                placeholder="Search email, name, or order ID"
              />
              <select style={input} value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)}>
                <option value="all">All Payments</option>
                <option value="paid">Paid</option>
                <option value="initiated">Initiated</option>
                <option value="unpaid">Unpaid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>

              <select style={input} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="all">All Orders</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <button style={smallBtn} onClick={syncOrderPayments}>Refresh / Sync Stripe</button>
            </div>
          </div>

          {orders.length === 0 && <p style={muted}>No orders found for this filter.</p>}

          {orders.map(o => (
            <div key={o.id} className="admin-order-card">
              <div>
                <b>#{o.id?.slice(0,8).toUpperCase()}</b><br />
                <span>{o.address?.full_name || "No name"}</span><br />
                <span style={muted}>{o.address?.email}</span><br />
                <b>${o.total}</b>
              </div>

              <div className="admin-order-status">
                <div><b>Payment:</b> {o.payment_status}</div>
                <div><b>Order:</b> {o.status}</div>
                <div><b>Stripe:</b> {o.stripe_session_status || "-"}</div>
                <div><b>Created:</b> {formatWIB(o.stripe_created)}</div>
                <div><b>Expires:</b> {formatWIB(o.stripe_expires_at)}</div>
                <div style={{ color: stripeCountdownColor(o.stripe_expires_at), fontWeight: 800 }}>
                  <b>Countdown:</b> {stripeCountdown(o.stripe_expires_at)}
                </div>
                {o.tracking_number && <div><b>Tracking:</b> {o.tracking_number}</div>}
              </div>

              <div className="admin-order-action">
                <button style={smallBtn} onClick={() => openOrder(o)}>
                  View / Edit
                </button>
                <button style={dangerBtn} onClick={() => deleteOrder(o.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}

          {selectedOrder && (
            <div style={modalOverlay}>
              <div style={modalBox}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                  <h2 style={{ fontSize:24, fontWeight:800 }}>Order #{selectedOrder.id?.slice(0,8)}</h2>
                  <button style={dangerBtn} onClick={() => setSelectedOrder(null)}>Close</button>
                </div>

                <div style={modalGrid}>
                  <div style={panel}>
                    <h3 style={h3}>Customer & Shipping Address</h3>
                    <p><b>Name:</b> {selectedOrder.address?.full_name}</p>
                    <p><b>Email:</b> {selectedOrder.address?.email}</p>
                    <p><b>Phone:</b> {selectedOrder.address?.phone}</p>
                    <p><b>Country:</b> {selectedOrder.address?.country}</p>
                    <p><b>State:</b> {selectedOrder.address?.state}</p>
                    <p><b>City:</b> {selectedOrder.address?.city}</p>
                    <p><b>Postal Code:</b> {selectedOrder.address?.postal_code}</p>
                    <p><b>Address:</b><br />{selectedOrder.address?.address}</p>
                  </div>

                  <div style={panel}>
                    <h3 style={h3}>Order Control</h3>

                    <label style={label}>Payment Status</label>
                    <select
                      style={input}
                      value={selectedOrder.payment_status}
                      onChange={e => setSelectedOrder({...selectedOrder, payment_status:e.target.value})}
                    >
                      <option value="initiated">initiated</option>
                      <option value="unpaid">unpaid</option>
                      <option value="paid">paid</option>
                      <option value="failed">failed</option>
                      <option value="refunded">refunded</option>
                    </select>

                    <label style={label}>Order Status</label>
                    <select
                      style={input}
                      value={selectedOrder.status}
                      onChange={e => setSelectedOrder({...selectedOrder, status:e.target.value})}
                    >
                      <option value="pending">pending</option>
                      <option value="processing">processing</option>
                      <option value="packed">packed</option>
                      <option value="shipped">shipped</option>
                      <option value="delivered">delivered</option>
                      <option value="cancelled">cancelled</option>
                    </select>

                    <Input label="Courier" value={selectedOrder.courier} onChange={v => setSelectedOrder({...selectedOrder, courier:v})} />
                    <Input label="Tracking Number" value={selectedOrder.tracking_number} onChange={v => setSelectedOrder({...selectedOrder, tracking_number:v})} />

                    <label style={label}>Admin Note</label>
                    <textarea
                      style={textarea}
                      value={selectedOrder.admin_note}
                      onChange={e => setSelectedOrder({...selectedOrder, admin_note:e.target.value})}
                    />

                    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                      <button style={primaryBtn} onClick={saveSelectedOrder}>Save Order</button>
                      {selectedOrder.payment_method === "bank" && selectedOrder.bank_proof_url && (
                        <button style={primaryBtn} onClick={() => verifyBankOrder(selectedOrder.id)}>
                          Verify Bank Payment
                        </button>
                      )}
                      {selectedOrder.payment_method === "bank" && selectedOrder.bank_proof_url && (
                        <button style={smallBtn} onClick={() => rejectBankOrder(selectedOrder.id)}>
                          Reject Proof
                        </button>
                      )}
                      <button style={dangerBtn} onClick={() => deleteOrder(selectedOrder.id)}>
                        Delete Order
                      </button>
                    </div>
                  </div>
                </div>

                <div style={panel}>
                  <h3 style={h3}>Items</h3>
                  {(selectedOrder.lines || []).map((item, idx) => (
                    <div key={idx} style={itemRow}>
                      <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                        {item.image && <img src={item.image} alt="" style={{ width:60, height:60, objectFit:"cover", borderRadius:8 }} />}
                        <div>
                          <b>{item.name}</b><br />
                          <span style={muted}>SKU: {item.sku} • Weight: {item.weight}</span>
                        </div>
                      </div>
                      <div>
                        Qty: {item.qty}<br />
                        ${item.line_total}
                      </div>
                    </div>
                  ))}
                </div>

                <div style={modalGrid}>
                  <div style={panel}>
                    <h3 style={h3}>Payment</h3>
                    <p><b>Method:</b> {selectedOrder.payment_method}</p>
                    {selectedOrder.payment_method === "bank" && (
                      <div style={{ margin:"12px 0", padding:12, border:"1px solid #333", borderRadius:10 }}>
                        <p><b>Bank Proof:</b> {selectedOrder.bank_proof_url ? "Uploaded" : "Not uploaded"}</p>
                        {selectedOrder.bank_proof_uploaded_at && <p><b>Uploaded:</b> {selectedOrder.bank_proof_uploaded_at}</p>}
                        {selectedOrder.bank_proof_url && (
                          <a href={selectedOrder.bank_proof_url} target="_blank" rel="noreferrer" style={link}>
                            View Payment Proof
                          </a>
                        )}
                      </div>
                    )}
                    <p><b>Stripe Session:</b><br />{selectedOrder.stripe_session_id || "-"}</p>
                    <p><b>Stripe Status:</b> {selectedOrder.stripe_session_status || "-"}</p>
                    <p><b>Stripe Payment:</b> {selectedOrder.stripe_payment_status || selectedOrder.payment_status || "-"}</p>
                    <p><b>Stripe Created:</b><br />{formatWIB(selectedOrder.stripe_created)}</p>
                    <p><b>Stripe Expires:</b><br />{formatWIB(selectedOrder.stripe_expires_at)}</p>
                    <p style={{ color: stripeCountdownColor(selectedOrder.stripe_expires_at), fontWeight:800 }}>
                      <b>Countdown:</b> {stripeCountdown(selectedOrder.stripe_expires_at)}
                    </p>
                    <p><b>Order Created:</b> {selectedOrder.created_at}</p>
                  </div>

                  <div style={panel}>
                    <h3 style={h3}>Totals</h3>
                    <p><b>Subtotal:</b> ${selectedOrder.subtotal}</p>
                    <p><b>Shipping:</b> ${selectedOrder.shipping}</p>
                    <p><b>Tax:</b> ${selectedOrder.tax}</p>
                    <p><b>Total:</b> ${selectedOrder.total}</p>
                    <p><b>Carrier:</b> {selectedOrder.shipping_info?.carrier}</p>
                    <p><b>Service:</b> {selectedOrder.shipping_info?.service}</p>
                    <p><b>Billable KG:</b> {selectedOrder.shipping_info?.billable_kg}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "promotions" && (
        <div>
          <h2 style={h2}>{editingPromo ? "Edit Promotion" : "Create Promotion"}</h2>

          <div style={panel}>
            <div style={formGrid}>
              <Input
                label="Promo Title"
                value={promoForm.title}
                onChange={v => setPromoForm({...promoForm, title:v})}
              />

              <Input
                label="Coupon Code"
                value={promoForm.code}
                onChange={v => setPromoForm({...promoForm, code:v.toUpperCase()})}
              />

              <div>
                <label style={label}>Discount Type</label>
                <select
                  style={input}
                  value={promoForm.type}
                  onChange={e => setPromoForm({...promoForm, type:e.target.value})}
                >
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed Amount $</option>
                </select>
              </div>

              <Input
                label={promoForm.type === "fixed" ? "Discount Amount $" : "Discount Percent %"}
                value={promoForm.value}
                onChange={v => setPromoForm({...promoForm, value:v})}
              />

              <Input
                label="Minimum Order $"
                value={promoForm.minimum_order}
                onChange={v => setPromoForm({...promoForm, minimum_order:v})}
              />

              <Input
                label="Banner Text"
                value={promoForm.banner_text}
                onChange={v => setPromoForm({...promoForm, banner_text:v})}
              />

              <Input
                label="Start Date ISO optional"
                value={promoForm.start_at}
                onChange={v => setPromoForm({...promoForm, start_at:v})}
              />

              <Input
                label="End Date ISO optional"
                value={promoForm.end_at}
                onChange={v => setPromoForm({...promoForm, end_at:v})}
              />
            </div>

            <div className="promo-toggle-row">
              <label className="promo-toggle-card">
                <input
                  type="checkbox"
                  checked={promoForm.active}
                  onChange={e => setPromoForm({...promoForm, active:e.target.checked})}
                />
                <span>
                  <b>Active</b>
                  <small>Coupon can be used at checkout</small>
                </span>
              </label>

              <label className="promo-toggle-card">
                <input
                  type="checkbox"
                  checked={promoForm.show_banner}
                  onChange={e => setPromoForm({...promoForm, show_banner:e.target.checked})}
                />
                <span>
                  <b>Show website banner</b>
                  <small>Display promo on the top announcement bar</small>
                </span>
              </label>
            </div>

            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button style={primaryBtn} onClick={savePromo}>
                {editingPromo ? "Update Promo" : "Create Promo"}
              </button>

              {editingPromo && (
                <button
                  style={dangerBtn}
                  onClick={() => {
                    setEditingPromo(null);
                    setPromoForm(emptyPromo);
                  }}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          <h2 style={h2}>Promotion List</h2>

          {promos.length === 0 && <p style={muted}>No promotions yet.</p>}

          {promos.map(promo => (
            <div key={promo.id} className="admin-order-card">
              <div>
                <b>{promo.title}</b><br />
                <span style={muted}>Code: {promo.code || "No code"}</span><br />
                <span style={muted}>Min order: ${Number(promo.minimum_order || 0).toFixed(2)}</span><br />
                {promo.banner_text && <span style={muted}>Banner: {promo.banner_text}</span>}
              </div>

              <div className="admin-order-status">
                <div><b>Discount:</b> {promo.type === "fixed" ? `$${promo.value}` : `${promo.value}%`}</div>
                <div><b>Status:</b> {promo.active ? "ON" : "OFF"}</div>
                <div><b>Banner:</b> {promo.show_banner ? "ON" : "OFF"}</div>
              </div>

              <div className="admin-order-action" style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <button style={smallBtn} onClick={() => togglePromo(promo)}>
                  {promo.active ? "Turn Off" : "Turn On"}
                </button>

                <button style={smallBtn} onClick={() => togglePromoBanner(promo)}>
                  {promo.show_banner ? "Hide Banner" : "Show Banner"}
                </button>

                <button style={smallBtn} onClick={() => editPromo(promo)}>
                  Edit
                </button>

                <button style={dangerBtn} onClick={() => deletePromo(promo.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "media" && (
        <div>
          <div className="admin-orders-toolbar">
            <h2 style={h2}>Media Manager</h2>
            <button style={smallBtn} onClick={loadMedia}>Refresh</button>
          </div>

          <p style={muted}>
            Files shown here are stored in /uploads on the VPS. Deleting a file here removes it permanently from storage.
          </p>

          <div className="media-upload-panel">
            <label className="media-upload-box">
              <strong>Upload Images</strong>
              <span>Select one or multiple images. Progress will appear below.</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  uploadMediaFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>

          </div>

          {mediaUploadStatus && (
            <div style={{
              margin:"12px 0",
              padding:12,
              background:"#0b0b0b",
              border:"1px solid #333",
              borderRadius:10,
              color: mediaUploadStatus.toLowerCase().includes("failed") ? "#FCA5A5" : "#C8A97E",
              fontWeight:700,
              opacity: uploadStatusFading ? 0 : 1,
              transition:"opacity .9s ease"
            }}>
              {mediaUploadStatus}
            </div>
          )}

          {media.length === 0 && (
            <div style={panel}>
              <p style={muted}>No uploaded files found.</p>
            </div>
          )}

          <div className="media-grid">
            {media.map((file) => {
              const usageList = Array.isArray(file?.usage) ? file.usage : [];
              const usageLabel = usageList.length ? usageList.join(" + ") : "Unused";
              const modifiedDate = file?.modified_at
                ? new Date(Number(file.modified_at) < 9999999999 ? Number(file.modified_at) * 1000 : file.modified_at).toLocaleString()
                : "-";

              return (
                <div key={file.url} className="media-card">
                  <div className="media-preview" onClick={() => setMediaViewer(file)}>
                    <img
                      src={`${file.url}?v=${file.modified_at || Date.now()}`}
                      alt={file.name}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <span className={`media-badge ${file?.is_used ? "used" : "unused"}`}>
                      {usageLabel}
                    </span>
                  </div>

                  <div className="media-info">
                    <b title={file.name}>{file.name}</b>
                    <span>{formatBytes(file.size)}</span>
                    <span>{modifiedDate}</span>
                  </div>

                  <div className="media-actions">
                    <button style={smallBtn} onClick={() => setMediaViewer(file)}>
                      View
                    </button>

                    <button
                      style={smallBtn}
                      onClick={() => navigator.clipboard.writeText(file.url)}
                    >
                      Copy URL
                    </button>

                    <button
                      style={dangerBtn}
                      onClick={() => confirmDeleteMediaFile(file)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {mediaViewer && (
            <div className="media-viewer" onClick={() => setMediaViewer(null)}>
              <div className="media-viewer-inner" onClick={(e) => e.stopPropagation()}>
                <div className="admin-orders-toolbar">
                  <h3>{mediaViewer.name}</h3>
                  <button style={smallBtn} onClick={() => setMediaViewer(null)}>Close</button>
                </div>
                <img src={`${mediaViewer.url}?v=${mediaViewer.modified_at || Date.now()}`} alt={mediaViewer.name} />
                <p style={muted}>{mediaViewer.url}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div>
          <h2 style={h2}>Store Settings</h2>

          <div style={panel}>
            <div style={formGrid}>
              <Input
                label="Store Name"
                value={settings.store_name || ""}
                onChange={v => setSettings({...settings, store_name:v})}
              />

              <Input
                label="Support Email"
                value={settings.support_email || ""}
                onChange={v => setSettings({...settings, support_email:v})}
              />

              <Input
                label="WhatsApp / iMessage"
                value={settings.whatsapp || ""}
                onChange={v => setSettings({...settings, whatsapp:v})}
              />

              <div>
                <label style={label}>Homepage Banner Image URL</label>
                <input
                  style={input}
                  value={settings.homepage_banner || ""}
                  onChange={e => setSettings({...settings, homepage_banner:e.target.value})}
                  placeholder="Leave empty to use default hero image"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => uploadSettingImage(e, "homepage_banner")}
                  style={{ marginTop:10 }}
                />
                {settings.homepage_banner && (
                  <div style={{ marginTop:12 }}>
                    <img
                      src={settings.homepage_banner}
                      alt="Homepage banner preview"
                      style={{ width:"100%", maxHeight:180, objectFit:"cover", borderRadius:12, border:"1px solid #333" }}
                    />
                    <button
                      style={{ ...dangerBtn, marginTop:10 }}
                      onClick={async () => {
                        const oldPath = settings.homepage_banner;
                        if (oldPath?.startsWith("/uploads/")) {
                          await fetch(`${API}/admin/upload?path=${encodeURIComponent(oldPath)}`, {
                            method:"DELETE",
                            headers: authHeaders
                          });
                        }
                        setSettings({...settings, homepage_banner:""});
                      }}
                    >
                      Use Default Banner
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop:14 }}>
              <label style={label}>Announcement Bar Text</label>
              <textarea
                style={textarea}
                value={settings.announcement || ""}
                onChange={e => setSettings({...settings, announcement:e.target.value})}
                placeholder="Example: Black Friday Sale — Use SALE10"
              />
            </div>

            <div className="promo-toggle-row">
              <label className="promo-toggle-card">
                <input
                  type="checkbox"
                  checked={!!settings.announcement_active}
                  onChange={e => setSettings({...settings, announcement_active:e.target.checked})}
                />
                <span>
                  <b>Show announcement bar</b>
                  <small>Display this text at the top of the website</small>
                </span>
              </label>

              <label className="promo-toggle-card">
                <input
                  type="checkbox"
                  checked={!!settings.maintenance_mode}
                  onChange={e => setSettings({...settings, maintenance_mode:e.target.checked})}
                />
                <span>
                  <b>Maintenance mode</b>
                  <small>Keep this off unless you want to pause the store</small>
                </span>
              </label>
            </div>


            <div style={{marginTop:20}}>
              <h3 style={{marginBottom:10}}>Security</h3>
              <div style={formGrid}>
                <Input label="Turnstile Site Key" value={settings.turnstile_site_key || ""} onChange={v => setSettings({...settings, turnstile_site_key:v})}/>
                <Input label="Turnstile Secret Key" value={settings.turnstile_secret_key || ""} onChange={v => setSettings({...settings, turnstile_secret_key:v})}/>
              </div>
              <label className="promo-toggle-card" style={{marginTop:10}}>
                <input type="checkbox" checked={!!settings.turnstile_enabled}
                  onChange={e => setSettings({...settings, turnstile_enabled:e.target.checked})}/>
                <span><b>Enable Turnstile</b></span>
              </label>
            </div>

            <div style={{marginTop:24}}>
              <h3 style={{marginBottom:10}}>Stripe</h3>
              <div style={formGrid}>
                <Input label="Publishable Key" value={settings.stripe_publishable_key || ""} onChange={v => setSettings({...settings, stripe_publishable_key:v})}/>
                <Input label="Secret Key" value={settings.stripe_secret_key || ""} onChange={v => setSettings({...settings, stripe_secret_key:v})}/>
                <Input label="Webhook Secret" value={settings.stripe_webhook_secret || ""} onChange={v => setSettings({...settings, stripe_webhook_secret:v})}/>
              </div>
              <label className="promo-toggle-card" style={{marginTop:10}}>
                <input type="checkbox" checked={!!settings.stripe_enabled}
                  onChange={e => setSettings({...settings, stripe_enabled:e.target.checked})}/>
                <span><b>Enable Stripe</b></span>
              </label>
            </div>

            <div style={{marginTop:24}}>
              <h3 style={{marginBottom:10}}>PayPal</h3>
              <div style={formGrid}>
                <Input label="Client ID" value={settings.paypal_client_id || ""} onChange={v => setSettings({...settings, paypal_client_id:v})}/>
                <Input label="Secret" value={settings.paypal_secret || ""} onChange={v => setSettings({...settings, paypal_secret:v})}/>
              </div>
              <label className="promo-toggle-card" style={{marginTop:10}}>
                <input type="checkbox" checked={!!settings.paypal_enabled}
                  onChange={e => setSettings({...settings, paypal_enabled:e.target.checked})}/>
                <span><b>Enable PayPal</b></span>
              </label>
            </div>

            <div style={{marginTop:24}}>
              <h3 style={{marginBottom:10}}>Bank Transfer</h3>
              <div style={formGrid}>
                <Input label="Bank Name" value={settings.bank_name || ""} onChange={v => setSettings({...settings, bank_name:v})}/>
                <Input label="Account Name" value={settings.bank_account_name || ""} onChange={v => setSettings({...settings, bank_account_name:v})}/>
                <Input label="Account Number" value={settings.bank_account_number || ""} onChange={v => setSettings({...settings, bank_account_number:v})}/>
                <Input label="SWIFT / Routing" value={settings.bank_swift || ""} onChange={v => setSettings({...settings, bank_swift:v})}/>
              </div>

              <div style={{marginTop:10}}>
                <label style={label}>Bank Notes</label>
                <textarea style={textarea}
                  value={settings.bank_notes || ""}
                  onChange={e => setSettings({...settings, bank_notes:e.target.value})}/>
              </div>

              <label className="promo-toggle-card" style={{marginTop:10}}>
                <input type="checkbox" checked={!!settings.bank_enabled}
                  onChange={e => setSettings({...settings, bank_enabled:e.target.checked})}/>
                <span><b>Enable Bank Transfer</b></span>
              </label>
            </div>

            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:18 }}>
              <button style={primaryBtn} onClick={saveSettings}>Save Settings</button>
            </div>

          </div>

          <h2 style={h2}>Preview</h2>
          <div style={panel}>
            <p style={muted}>Announcement bar preview:</p>
            <div style={{
              background:"#C8A97E",
              color:"#000",
              padding:"10px 14px",
              textAlign:"center",
              fontWeight:800,
              borderRadius:8,
              marginTop:10
            }}>
              {settings.announcement_active && settings.announcement
                ? settings.announcement
                : "Announcement bar is off"}
            </div>
          </div>
        </div>
      )}

    </AdminShell>
  );
}

function AdminShell({ children }) {
  return (
    <div style={{ background:"#080908", color:"#fff", minHeight:"100vh", padding:"clamp(14px, 3vw, 30px)", fontStyle:"normal" }}>
      <style>{`


        .image-source-box {
          margin-top: 10px;
          border: 1px solid #333;
          background: #0d0d0d;
          border-radius: 12px;
          padding: 14px;
        }

        .image-source-title {
          font-weight: 800;
          margin-bottom: 10px;
          color: #fff;
        }

        .image-source-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .image-source-btn {
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-weight: 800;
          padding: 12px 14px;
          border-radius: 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
        }

        .image-source-btn input {
          display: none;
        }

        .image-source-help {
          color: #aaa;
          font-size: 12px;
          margin-top: 8px;
        }

        @media (max-width: 760px) {
          .image-source-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .image-source-btn {
            width: 100%;
          }
        }



        .image-choice-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0,0,0,.78);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .image-choice-modal {
          width: min(520px, 94vw);
          background: #111;
          border: 1px solid #333;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 20px 60px rgba(0,0,0,.45);
        }

        .image-choice-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .image-choice-head h3 {
          margin: 0;
        }

        .image-choice-head button {
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-size: 22px;
          font-weight: 900;
          width: 42px;
          height: 42px;
          cursor: pointer;
        }

        .image-choice-modal p {
          color: #aaa;
          margin: 0 0 16px;
        }

        .image-choice-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .image-choice-actions button {
          min-height: 54px;
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-weight: 900;
          cursor: pointer;
          padding: 12px;
        }

        @media (max-width: 760px) {
          .image-choice-actions {
            grid-template-columns: 1fr;
          }
        }



        

.clean-file-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex: 1 1 100%;
  box-sizing: border-box;
  margin-top: 10px;
  border: 1px solid #333;
  background: #111;
  padding: 10px 14px;
  min-height: 54px;
}

.clean-file-button {
  background: #fff;
  color: #000;
  border: 1px solid #ddd;
  padding: 8px 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.2;
}

.clean-file-picker span {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}




        .product-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
        }

        .product-gallery-card {
          position: relative;
          background: #0b0b0b;
          border: 1px solid #333;
          border-radius: 12px;
          padding: 8px;
          min-height: 170px;
        }

        .product-gallery-img {
          width: 100%;
          height: 110px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #222;
          display: block;
        }

        .product-gallery-missing {
          width: 100%;
          height: 110px;
          display: none;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px dashed #555;
          color: #aaa;
          font-size: 12px;
          text-align: center;
        }

        .product-gallery-actions {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }

        .product-gallery-actions button {
          flex: 1;
          padding: 7px 8px;
          border: 0;
          background: #fff;
          color: #000;
          font-weight: 800;
          cursor: pointer;
          border-radius: 6px;
          font-size: 12px;
        }

        .product-gallery-actions button.danger {
          background: #351313;
          color: #fff;
          border: 1px solid #773333;
        }

        .main-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: #C8A97E;
          color: #000;
          font-size: 10px;
          font-weight: 900;
          padding: 4px 6px;
          border-radius: 6px;
        }

        .product-feature-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          border: 1px solid #2a2a2a;
          background: #0b0b0b;
          padding: 14px;
          border-radius: 12px;
          cursor: pointer;
          margin: 16px 0;
          max-width: 420px;
        }

        .product-feature-card input {
          margin-top: 4px;
          width: 18px;
          height: 18px;
          flex: 0 0 auto;
        }

        .product-feature-card span {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .product-feature-card small {
          color: #aaa;
          font-size: 12px;
          line-height: 1.35;
        }



        .media-upload-panel {
          border: 1px solid #252525;
          background: #111;
          border-radius: 14px;
          padding: 16px;
          margin: 18px 0;
          max-width: 780px;
        }

        .media-upload-box {
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px dashed #444;
          border-radius: 12px;
          padding: 18px;
          cursor: pointer;
        }

        .media-upload-box input {
          margin-top: 10px;
        }

        .media-upload-list {
          display: grid;
          gap: 10px;
          margin-top: 14px;
        }

        .media-upload-row {
          display: grid;
          grid-template-columns: 1fr 180px 50px;
          gap: 10px;
          align-items: center;
          background: #080808;
          border: 1px solid #222;
          border-radius: 10px;
          padding: 10px;
        }

        .media-upload-row div {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .media-upload-row span {
          color: #aaa;
          font-size: 12px;
        }

        .media-upload-row.success span {
          color: #6ee784;
        }

        .media-upload-row.error span {
          color: #ff7777;
        }

        .media-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: #333;
          color: #ddd;
          font-size: 11px;
          font-weight: 800;
          padding: 5px 8px;
          border-radius: 999px;
          z-index: 2;
        }

        .media-badge.used {
          background: #fff;
          color: #000;
        }

        .media-badge.unused {
          background: #333;
          color: #bbb;
        }

        .media-viewer {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0,0,0,.85);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .product-media-picker {
          width: 92vw;
        }

        .media-viewer-inner {
          max-width: 92vw;
          max-height: 92vh;
          overflow: auto;
          background: #111;
          border: 1px solid #333;
          border-radius: 16px;
          padding: 18px;
        }

        .media-viewer-inner img {
          display: block;
          max-width: 100%;
          max-height: 72vh;
          object-fit: contain;
          margin: 12px auto;
        }

        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 14px;
          margin-top: 18px;
        }

        .media-card {
          background: #111;
          border: 1px solid #222;
          border-radius: 14px;
          padding: 12px;
          min-width: 0;
        }

        .media-preview {
          position: relative;
          cursor: pointer;
          width: 100%;
          height: 150px;
          background: #070707;
          border: 1px solid #222;
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        .media-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .media-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          margin-bottom: 10px;
        }

        .media-info b {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .media-info span {
          color: #aaa;
          font-size: 12px;
        }

        .media-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .media-actions button {
          flex: 1;
        }

        .admin-orders-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
          margin-bottom: 15px;
        }

        .admin-filter-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .admin-order-card {
          display: grid;
          grid-template-columns: 1.4fr 1fr auto;
          align-items: center;
          gap: 20px;
          padding: 18px;
          border: 1px solid #222;
          margin-top: 10px;
          border-radius: 12px;
          background: #111;
        }

        .admin-order-status {
          justify-self: start;
          min-width: 220px;
        }

        .admin-order-action {
          justify-self: end;
        }


        .promo-toggle-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 12px;
          margin: 18px 0;
        }

        .promo-toggle-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          border: 1px solid #2a2a2a;
          background: #0b0b0b;
          padding: 14px;
          border-radius: 12px;
          cursor: pointer;
          min-width: 0;
        }

        .promo-toggle-card input {
          margin-top: 4px;
          width: 18px;
          height: 18px;
          flex: 0 0 auto;
        }

        .promo-toggle-card span {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .promo-toggle-card b {
          font-size: 15px;
          line-height: 1.2;
          white-space: normal;
        }

        .promo-toggle-card small {
          color: #aaa;
          font-size: 12px;
          line-height: 1.35;
        }

        @media (max-width: 760px) {
  

        .image-source-box {
          margin-top: 10px;
          border: 1px solid #333;
          background: #0d0d0d;
          border-radius: 12px;
          padding: 14px;
        }

        .image-source-title {
          font-weight: 800;
          margin-bottom: 10px;
          color: #fff;
        }

        .image-source-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }

        .image-source-btn {
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-weight: 800;
          padding: 12px 14px;
          border-radius: 0;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
        }

        .image-source-btn input {
          display: none;
        }

        .image-source-help {
          color: #aaa;
          font-size: 12px;
          margin-top: 8px;
        }

        @media (max-width: 760px) {
          .image-source-actions {
            flex-direction: column;
            align-items: stretch;
          }

          .image-source-btn {
            width: 100%;
          }
        }



        .image-choice-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0,0,0,.78);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .image-choice-modal {
          width: min(520px, 94vw);
          background: #111;
          border: 1px solid #333;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 20px 60px rgba(0,0,0,.45);
        }

        .image-choice-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .image-choice-head h3 {
          margin: 0;
        }

        .image-choice-head button {
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-size: 22px;
          font-weight: 900;
          width: 42px;
          height: 42px;
          cursor: pointer;
        }

        .image-choice-modal p {
          color: #aaa;
          margin: 0 0 16px;
        }

        .image-choice-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .image-choice-actions button {
          min-height: 54px;
          border: 1px solid #333;
          background: #fff;
          color: #000;
          font-weight: 900;
          cursor: pointer;
          padding: 12px;
        }

        @media (max-width: 760px) {
          .image-choice-actions {
            grid-template-columns: 1fr;
          }
        }



        

.clean-file-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex: 1 1 100%;
  box-sizing: border-box;
  margin-top: 10px;
  border: 1px solid #333;
  background: #111;
  padding: 10px 14px;
  min-height: 54px;
}

.clean-file-button {
  background: #fff;
  color: #000;
  border: 1px solid #ddd;
  padding: 8px 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.2;
}

.clean-file-picker span {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}




        .product-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
        }

        .product-gallery-card {
          position: relative;
          background: #0b0b0b;
          border: 1px solid #333;
          border-radius: 12px;
          padding: 8px;
          min-height: 170px;
        }

        .product-gallery-img {
          width: 100%;
          height: 110px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #222;
          display: block;
        }

        .product-gallery-missing {
          width: 100%;
          height: 110px;
          display: none;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px dashed #555;
          color: #aaa;
          font-size: 12px;
          text-align: center;
        }

        .product-gallery-actions {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }

        .product-gallery-actions button {
          flex: 1;
          padding: 7px 8px;
          border: 0;
          background: #fff;
          color: #000;
          font-weight: 800;
          cursor: pointer;
          border-radius: 6px;
          font-size: 12px;
        }

        .product-gallery-actions button.danger {
          background: #351313;
          color: #fff;
          border: 1px solid #773333;
        }

        .main-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: #C8A97E;
          color: #000;
          font-size: 10px;
          font-weight: 900;
          padding: 4px 6px;
          border-radius: 6px;
        }

        .product-feature-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          border: 1px solid #2a2a2a;
          background: #0b0b0b;
          padding: 14px;
          border-radius: 12px;
          cursor: pointer;
          margin: 16px 0;
          max-width: 420px;
        }

        .product-feature-card input {
          margin-top: 4px;
          width: 18px;
          height: 18px;
          flex: 0 0 auto;
        }

        .product-feature-card span {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .product-feature-card small {
          color: #aaa;
          font-size: 12px;
          line-height: 1.35;
        }



        .media-upload-panel {
          border: 1px solid #252525;
          background: #111;
          border-radius: 14px;
          padding: 16px;
          margin: 18px 0;
          max-width: 780px;
        }

        .media-upload-box {
          display: flex;
          flex-direction: column;
          gap: 6px;
          border: 1px dashed #444;
          border-radius: 12px;
          padding: 18px;
          cursor: pointer;
        }

        .media-upload-box input {
          margin-top: 10px;
        }

        .media-upload-list {
          display: grid;
          gap: 10px;
          margin-top: 14px;
        }

        .media-upload-row {
          display: grid;
          grid-template-columns: 1fr 180px 50px;
          gap: 10px;
          align-items: center;
          background: #080808;
          border: 1px solid #222;
          border-radius: 10px;
          padding: 10px;
        }

        .media-upload-row div {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .media-upload-row span {
          color: #aaa;
          font-size: 12px;
        }

        .media-upload-row.success span {
          color: #6ee784;
        }

        .media-upload-row.error span {
          color: #ff7777;
        }

        .media-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          background: #333;
          color: #ddd;
          font-size: 11px;
          font-weight: 800;
          padding: 5px 8px;
          border-radius: 999px;
          z-index: 2;
        }

        .media-badge.used {
          background: #fff;
          color: #000;
        }

        .media-badge.unused {
          background: #333;
          color: #bbb;
        }

        .media-viewer {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0,0,0,.85);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .product-media-picker {
          width: 92vw;
        }

        .media-viewer-inner {
          max-width: 92vw;
          max-height: 92vh;
          overflow: auto;
          background: #111;
          border: 1px solid #333;
          border-radius: 16px;
          padding: 18px;
        }

        .media-viewer-inner img {
          display: block;
          max-width: 100%;
          max-height: 72vh;
          object-fit: contain;
          margin: 12px auto;
        }

        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 14px;
          margin-top: 18px;
        }

        .media-card {
          background: #111;
          border: 1px solid #222;
          border-radius: 14px;
          padding: 12px;
          min-width: 0;
        }

        .media-preview {
          position: relative;
          cursor: pointer;
          width: 100%;
          height: 150px;
          background: #070707;
          border: 1px solid #222;
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        .media-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .media-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          margin-bottom: 10px;
        }

        .media-info b {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .media-info span {
          color: #aaa;
          font-size: 12px;
        }

        .media-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .media-actions button {
          flex: 1;
        }

        .admin-orders-toolbar {
            align-items: stretch;
          }

          .admin-filter-group {
            width: 100%;
            flex-direction: column;
          }

          .admin-filter-group select,
          .admin-filter-group button {
            width: 100%;
          }

          .admin-order-card {
            grid-template-columns: 1fr;
            align-items: start;
            gap: 12px;
          }

          .admin-order-status {
            min-width: 0;
            width: 100%;
            justify-self: stretch;
            padding: 12px;
            border: 1px solid #222;
            border-radius: 10px;
            background: #0b0b0b;
          }

          .admin-order-action {
            width: 100%;
            justify-self: stretch;
          }

          .admin-order-action button {
            width: 100%;
          }
        }
      `}</style>
      {children}
    </div>
  );
}

function Card({title,value}) {
  return (
    <div style={card}>
      <div style={muted}>{title}</div>
      <div style={{ fontSize:30, fontWeight:800 }}>{value}</div>
    </div>
  );
}

function Input({ label: l, value, onChange }) {
  return (
    <div>
      <label style={label}>{l}</label>
      <input style={input} value={value || ""} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function TextArea({ label: l, value, onChange }) {
  return (
    <div>
      <label style={label}>{l}</label>
      <textarea style={textarea} value={value || ""} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

const h1 = { fontSize:32, marginBottom:10 };
const h2 = { fontSize:22, margin:"25px 0 15px" };
const muted = { color:"#aaa", fontStyle:"normal" };
const link = { color:"#fff", textDecoration:"underline" };
const tabs = { display:"flex", gap:10, flexWrap:"wrap", marginBottom:25 };
const tabBtn = active => ({
  padding:"12px 18px",
  border:"1px solid #333",
  background: active ? "#fff" : "#111",
  color: active ? "#000" : "#fff",
  cursor:"pointer",
  fontWeight:700,
});
const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:15 };
const formGrid = { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:15 };
const card = { background:"#111", border:"1px solid #222", padding:20, borderRadius:14 };
const row = {
  display:"flex",
  justifyContent:"space-between",
  alignItems:"center",
  gap:20,
  padding:16,
  border:"1px solid #222",
  marginTop:10,
  borderRadius:12,
  background:"#111",
};
const label = { display:"block", color:"#bbb", margin:"12px 0 6px" };
const input = { width:"100%", padding:12, background:"#111", color:"#fff", border:"1px solid #333" };
const textarea = { ...input, minHeight:100 };
const primaryBtn = { padding:"12px 18px", background:"#fff", color:"#000", border:"0", fontWeight:800, cursor:"pointer" };
const smallBtn = { padding:"10px 14px", background:"#fff", color:"#000", border:"0", fontWeight:700, cursor:"pointer" };
const dangerBtn = { padding:"10px 14px", background:"#351313", color:"#fff", border:"1px solid #773333", fontWeight:700, cursor:"pointer" };
const modalOverlay = {
  position:"fixed",
  inset:0,
  background:"rgba(0,0,0,0.82)",
  zIndex:9999,
  overflow:"auto",
  padding:25,
};
const modalBox = {
  maxWidth:1200,
  width:"100%",
  boxSizing:"border-box",
  margin:"0 auto",
  background:"#0b0d0b",
  border:"1px solid #333",
  borderRadius:16,
  padding:"clamp(14px, 3vw, 24px)",
  overflowX:"hidden",
};
const modalGrid = {
  display:"grid",
  gridTemplateColumns:"repeat(auto-fit,minmax(min(280px,100%),1fr))",
  gap:16,
  marginBottom:16,
};
const panel = {
  background:"#111",
  border:"1px solid #222",
  borderRadius:12,
  padding:16,
  marginBottom:16,
  minWidth:0,
  overflowWrap:"anywhere",
  wordBreak:"break-word",
};
const h3 = { fontSize:18, fontWeight:800, marginBottom:12 };
const itemRow = {
  display:"flex",
  justifyContent:"space-between",
  alignItems:"center",
  borderTop:"1px solid #222",
  padding:"12px 0",
  gap:15,
};
const preview = { width:120, height:120, objectFit:"cover", borderRadius:12, marginTop:10, display:"block" };
const emptyImageBox = {
  marginTop:10,
  padding:14,
  border:"1px dashed #444",
  borderRadius:10,
  color:"#aaa",
  fontSize:12,
};
